/**
 * Shared creative overlay composition — geometry + Canvas2D drawer.
 * DOM renderer in CreativeOverlay.tsx must use the same normalized layout.
 *
 * Z-order (bottom → top), intentional and shared with live preview:
 * 1. stickers (slot order)
 * 2. transition flash/swipe
 * 3. caption (caller may draw via CaptionOverlay / result-card)
 * Reconstruction label + watermark/score are card chrome above this layer.
 */
import type {
  CreativeOverlayModel,
  CreativeTransitionState,
} from "./creative-render-state.js";
import type { PreviewCaptionState } from "./caption-preview.js";
import {
  layoutSticker,
  stickerLayout,
  type StickerLayoutResult,
} from "./sticker-layout.js";
import { readyStickerArt } from "./sticker-art-cache.js";

export { layoutSticker, stickerLayout, type StickerLayoutResult };
export { STICKER_COLOR_HEX, stickerColor } from "./sticker-layout.js";
export { preloadStickerArt, preloadAllStickerArt } from "./sticker-art-cache.js";

export type OverlayRect = {
  x: number;
  y: number;
  w: number;
  h: number;
  rotationDeg: number;
  color: string;
  text: string;
  shape: StickerLayoutResult["shape"];
  /** Shell-only curated art override — draw the image instead of the
   *  procedural shape when set and preloaded (see sticker-art-cache.ts). */
  artSrc?: string;
};

export function transitionFlashOpacity(transition: CreativeTransitionState): number {
  if (transition.kind !== "flash") return 0;
  // Peak near start, fade out across authored duration.
  const p = transition.phase;
  return Math.max(0, Math.min(1, 1 - p)) * 0.92;
}

/** Swipe cover progress 0–1 (panel slides left→right uncovering). */
export function transitionSwipeProgress(transition: CreativeTransitionState): number {
  if (transition.kind !== "swipe") return 0;
  return Math.max(0, Math.min(1, transition.phase));
}

export interface ComposeOverlayOptions {
  /** When false, skip caption (result-card may draw it). Default true. */
  drawCaption?: boolean;
  /** Clip stickers to inset safe area. Default true. */
  clipSafeArea?: boolean;
}

/**
 * Draw stickers + transition (+ optional caption) onto an existing 2d context.
 * Caller provides the frame pixels already drawn.
 */
export function composeCreativeOverlay(
  ctx: CanvasRenderingContext2D,
  overlay: CreativeOverlayModel,
  width: number,
  height: number,
  options: ComposeOverlayOptions = {},
): void {
  hardenCanvas2d(ctx);
  const drawCaption = options.drawCaption !== false;
  const clipSafe = options.clipSafeArea !== false;

  ctx.save();
  if (clipSafe) {
    const padX = width * 0.04;
    const padY = height * 0.04;
    ctx.beginPath();
    ctx.rect(padX, padY, width - padX * 2, height - padY * 2);
    ctx.clip();
  }

  for (const sticker of overlay.stickers) {
    drawSticker(ctx, stickerLayout(sticker, width, height));
  }
  ctx.restore();

  drawTransition(ctx, overlay.transition, width, height);

  if (drawCaption) {
    drawCaptionOverlay(ctx, overlay.caption, width, height);
  }
}

/** Composite a frame + overlay into a new canvas. */
export function compositeFrameWithOverlay(
  frame: CanvasImageSource,
  overlay: CreativeOverlayModel,
  width: number,
  height: number,
  options?: ComposeOverlayOptions,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("2d context unavailable for creative overlay composite");
  }
  hardenCanvas2d(ctx);
  try {
    ctx.drawImage(frame, 0, 0, width, height);
  } catch {
    /* happy-dom / decode races */
  }
  composeCreativeOverlay(ctx, overlay, width, height, options);
  return canvas;
}

/**
 * happy-dom's CanvasRenderingContext2D is incomplete — fill in no-op stubs so
 * export/composite paths stay testable without claiming full browser parity.
 */
function hardenCanvas2d(ctx: CanvasRenderingContext2D): void {
  const c = ctx as CanvasRenderingContext2D & Record<string, unknown>;
  const noop = () => undefined;
  for (const key of [
    "save",
    "restore",
    "beginPath",
    "closePath",
    "clip",
    "fill",
    "stroke",
    "fillRect",
    "strokeRect",
    "fillText",
    "strokeText",
    "moveTo",
    "lineTo",
    "arc",
    "arcTo",
    "translate",
    "rotate",
    "scale",
    "rect",
    "drawImage",
  ] as const) {
    if (typeof c[key] !== "function") {
      c[key] = noop;
    }
  }
  if (typeof c.measureText !== "function") {
    c.measureText = () => ({ width: 0 }) as TextMetrics;
  }
}

function drawSticker(ctx: CanvasRenderingContext2D, layout: OverlayRect): void {
  ctx.save();
  ctx.translate(layout.x, layout.y);
  ctx.rotate((layout.rotationDeg * Math.PI) / 180);

  // Curated art override — draw the preloaded image and skip the procedural
  // shape entirely so canvas/export matches the DOM editor preview
  // (StickerGlyph draws the same `<img>` there). Falls back to the
  // procedural shape below when the art isn't ready (or has no override).
  const img = layout.artSrc ? readyStickerArt(layout.artSrc) : null;
  if (img) {
    ctx.drawImage(img, -layout.w / 2, -layout.h / 2, layout.w, layout.h);
    ctx.restore();
    return;
  }

  ctx.fillStyle = layout.color;
  ctx.strokeStyle = layout.color;
  ctx.lineWidth = Math.max(2, layout.w * 0.06);

  switch (layout.shape) {
    case "dot":
      ctx.beginPath();
      ctx.arc(0, 0, layout.w * 0.35, 0, Math.PI * 2);
      ctx.fill();
      break;
    case "star":
      drawStar(ctx, 0, 0, 5, layout.w * 0.45, layout.w * 0.2);
      ctx.fill();
      break;
    case "burst":
      drawStar(ctx, 0, 0, 8, layout.w * 0.5, layout.w * 0.22);
      ctx.globalAlpha = 0.92;
      ctx.fill();
      break;
    case "arrow":
      ctx.beginPath();
      ctx.moveTo(-layout.w * 0.45, 0);
      ctx.lineTo(layout.w * 0.15, 0);
      ctx.lineTo(layout.w * 0.15, -layout.h * 0.35);
      ctx.lineTo(layout.w * 0.5, 0);
      ctx.lineTo(layout.w * 0.15, layout.h * 0.35);
      ctx.lineTo(layout.w * 0.15, 0);
      ctx.closePath();
      ctx.fill();
      break;
    case "underline":
      ctx.fillRect(-layout.w / 2, -layout.h / 2, layout.w, layout.h);
      break;
    case "frame":
      ctx.strokeRect(-layout.w / 2, -layout.h / 2, layout.w, layout.h);
      break;
    case "badge":
      roundRect(ctx, -layout.w / 2, -layout.h / 2, layout.w, layout.h, layout.h * 0.35);
      ctx.fill();
      if (layout.text) {
        ctx.fillStyle = "#0b1220";
        ctx.font = `700 ${Math.round(layout.h * 0.38)}px Syne, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(layout.text, 0, 0);
      }
      break;
    case "text":
    default:
      ctx.font = `800 ${Math.round(layout.h * 0.55)}px Syne, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineWidth = Math.max(3, layout.w * 0.04);
      ctx.strokeStyle = "rgba(11,18,32,0.55)";
      ctx.strokeText(layout.text || "!", 0, 0);
      ctx.fillStyle = layout.color;
      ctx.fillText(layout.text || "!", 0, 0);
      break;
  }
  ctx.restore();
}

function drawTransition(
  ctx: CanvasRenderingContext2D,
  transition: CreativeTransitionState,
  width: number,
  height: number,
): void {
  if (transition.kind === "flash") {
    const a = transitionFlashOpacity(transition);
    if (a <= 0) return;
    ctx.save();
    ctx.globalAlpha = a;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
    return;
  }
  if (transition.kind === "swipe") {
    const p = transitionSwipeProgress(transition);
    if (p >= 1) return;
    const cover = (1 - p) * width;
    ctx.save();
    ctx.fillStyle = "rgba(11,18,32,0.92)";
    ctx.fillRect(width - cover, 0, cover, height);
    ctx.restore();
  }
}

/** Rainbow gradient stops for the KINETIC style's text fill — mirrors the DOM
 *  `.caption-overlay.style-kinetic` gradient in desk.css so preview == card.
 *  Exported so `result-card.ts` (the PUBLISHED still card's renderer) can
 *  reuse the exact same stops instead of drifting from a second copy. */
export const KINETIC_RAINBOW_STOPS = [
  "#ff3b30",
  "#ff9500",
  "#ffcc00",
  "#34c759",
  "#007aff",
  "#af52de",
  "#ff3b30",
];

/**
 * Draw the caption overlay with BOLDLY distinct per-style treatments — mirrors
 * `.caption-overlay.style-*` in desk.css exactly (font family/weight, color or
 * rainbow gradient fill, red band, tilt) so the canvas preview/export matches
 * the live DOM overlay. Style is visual only; the `text` op's `style` field and
 * its gameplay effect (landing emotion contribution) are unchanged.
 */
function drawCaptionOverlay(
  ctx: CanvasRenderingContext2D,
  caption: PreviewCaptionState,
  width: number,
  height: number,
): void {
  if (!caption.visible || caption.tokens.length === 0) return;
  const style = caption.style;
  const scale = (caption.scaleMilli / 1000) * caption.popScale;
  const cx = (caption.anchorXMilli / 1000) * width;
  const cy = (caption.anchorYMilli / 1000) * height;
  const boxW = Math.min(width - 24, width * 0.92);
  const boxH = Math.round(52 * scale);
  const fontSize = Math.round((style === "kinetic" ? 23 : 20) * scale);
  const rawText = caption.tokens.map((t) => t.text).join(" ");
  const text = style === "urgent" ? rawText.toUpperCase() : rawText;

  ctx.save();
  ctx.translate(cx, cy);
  if (style === "kinetic") ctx.rotate((-3 * Math.PI) / 180);

  // Background chrome per style — kinetic stays airy (no box), the others get
  // a bold band (documentary adds a thin "chyron" rule along the bottom edge).
  if (style === "urgent") {
    const band = ctx.createLinearGradient(0, -boxH / 2, 0, boxH / 2);
    band.addColorStop(0, "#e0201f");
    band.addColorStop(1, "#9c0d0d");
    ctx.fillStyle = band;
    ctx.fillRect(-boxW / 2, -boxH / 2, boxW, boxH);
  } else if (style === "documentary") {
    ctx.fillStyle = "rgba(17, 15, 11, 0.82)";
    ctx.fillRect(-boxW / 2, -boxH / 2, boxW, boxH);
    ctx.fillStyle = "rgba(244, 236, 216, 0.4)";
    ctx.fillRect(-boxW / 2, boxH / 2 - 2, boxW, 2);
  } else if (style === "clean") {
    ctx.fillStyle = "rgba(9, 14, 24, 0.3)";
    ctx.fillRect(-boxW / 2, -boxH / 2, boxW, boxH);
  }

  if (style === "documentary") {
    ctx.font = `400 ${fontSize}px Newsreader, serif`;
  } else if (style === "kinetic") {
    ctx.font = `italic 800 ${fontSize}px Syne, sans-serif`;
  } else if (style === "urgent") {
    ctx.font = `800 ${fontSize}px Syne, sans-serif`;
  } else {
    ctx.font = `500 ${fontSize}px Space Grotesk, sans-serif`;
  }
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.save();
  if (style === "urgent") ctx.scale(0.94, 1); // condensed feel
  if (style === "kinetic") {
    const half = Math.max(ctx.measureText(text).width / 2, 10);
    const rainbow = ctx.createLinearGradient(-half, 0, half, 0);
    KINETIC_RAINBOW_STOPS.forEach((c, i) =>
      rainbow.addColorStop(i / (KINETIC_RAINBOW_STOPS.length - 1), c),
    );
    ctx.fillStyle = rainbow;
  } else if (style === "documentary") {
    ctx.fillStyle = "#f2ebdd";
  } else {
    ctx.fillStyle = "#ffffff";
  }
  ctx.fillText(text, 0, 0, boxW - 16);
  ctx.restore();

  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  points: number,
  outer: number,
  inner: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = (Math.PI / 2) * -1 + (i * Math.PI) / points;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}
